/**
 * MapInterface - OpenLayers map component for flight planning
 * Handles map rendering, polygon drawing, and layer management
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useLatestRef } from '../hooks/useLatestRef';
import { Map, View, Overlay } from 'ol';
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer';
import { OSM, XYZ, Vector as VectorSource, TileWMS } from 'ol/source';
import TileArcGISRest from 'ol/source/TileArcGISRest';
import { get as getProjection, fromLonLat, toLonLat } from 'ol/proj';
import { defaults as defaultControls } from 'ol/control';
import { defaults as defaultInteractions } from 'ol/interaction';
import Draw from 'ol/interaction/Draw';
import Modify from 'ol/interaction/Modify';
import Snap from 'ol/interaction/Snap';
import { Fill, Stroke, Style, Circle as CircleStyle, Text } from 'ol/style';
import Feature from 'ol/Feature';
import { Polygon, Point, LineString } from 'ol/geom';
import { getArea as getGeodesicArea } from 'ol/sphere';

import {
	LuRuler,
	LuSquareDashed,
	LuCircle,
	LuTrash,
	LuX,
	LuMaximize,
	LuMapPin,
	LuSearch,
} from 'react-icons/lu';

import 'ol/ol.css';

// Norwegian airspace data
import {
	ZONE_TYPES,
	airports,
	militaryZones,
	heliports,
	governmentZones,
} from '../../../../../utils/map/norwegianAirspace';

// UTM grid utilities
import { generateGridSquares } from '../utils/utmGrid';

// Zoom level thresholds by grid size (meters) — sorted descending
const GRID_ZOOM_LEVELS = [
	{ minSize: 10000, zoom: 8 },
	{ minSize: 5000, zoom: 9 },
	{ minSize: 1000, zoom: 11 },
	{ minSize: 500, zoom: 13 },
	{ minSize: 100, zoom: 14 },
];

/** Get the appropriate zoom level for a given grid size. */
const getZoomForGridSize = (gridSize, fallback = 15) =>
	GRID_ZOOM_LEVELS.find(t => gridSize >= t.minSize)?.zoom ?? fallback;

const MapContainer = styled.div`
	width: 100%;
	height: 100%;
	position: relative;

	.ol-viewport {
		border-radius: 0;
	}
`;

const MapControls = styled.div`
	position: absolute;
	top: 10px;
	right: 10px;
	display: flex;
	flex-direction: column;
	gap: 5px;
	z-index: 10;
`;

const ControlButton = styled.button`
	width: 36px;
	height: 36px;
	border: none;
	border-radius: 4px;
	background-color: ${props =>
		props.$active ? props.theme.colors.green : 'rgba(40, 40, 50, 0.9)'};
	color: white;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 1.1rem;
	transition: background-color 0.2s;

	&:hover {
		background-color: ${props =>
			props.$active ? props.theme.colors.green : 'rgba(60, 60, 70, 0.9)'};
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`;

const ControlGroup = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
	background-color: rgba(40, 40, 50, 0.9);
	border-radius: 4px;
	padding: 2px;
`;

const LayerSelector = styled.div`
	display: flex;
`;

const LayerButton = styled.button`
	padding: 6px 12px;
	border: none;
	border-radius: 4px;
	background-color: ${props =>
		props.$active ? props.theme.colors.green : 'rgba(40, 40, 50, 0.9)'};
	color: white;
	cursor: pointer;
	font-size: 0.8rem;
	margin-right: 5px;

	&:hover {
		background-color: ${props =>
			props.$active ? props.theme.colors.green : 'rgba(60, 60, 70, 0.9)'};
	}
`;

const CoordinateDisplay = styled.div`
	background-color: rgba(40, 40, 50, 0.9);
	color: white;
	padding: 6px 10px;
	border-radius: 4px;
	font-size: 0.8rem;
	font-family: monospace;
	white-space: nowrap;
`;

const SearchContainer = styled.div`
	display: flex;
	gap: 4px;
`;

const BottomBar = styled.div`
	position: absolute;
	bottom: 10px;
	left: 10px;
	right: 10px;
	z-index: 10;
	display: flex;
	align-items: center;
	gap: 4px;
	pointer-events: none;

	& > * {
		pointer-events: auto;
	}
`;

const SearchInput = styled.input`
	width: 200px;
	padding: 6px 10px;
	border: none;
	border-radius: 4px;
	background-color: rgba(40, 40, 50, 0.9);
	color: white;
	font-size: 0.8rem;

	&::placeholder {
		color: #888;
	}

	&:focus {
		outline: none;
		box-shadow: 0 0 0 2px rgba(0, 255, 0, 0.3);
	}
`;

const SearchButton = styled.button`
	padding: 6px 10px;
	border: none;
	border-radius: 4px;
	background-color: rgba(40, 40, 50, 0.9);
	color: white;
	cursor: pointer;
	font-size: 0.8rem;

	&:hover {
		background-color: rgba(60, 60, 70, 0.9);
	}
`;

const MeasurementTools = styled.div`
	position: absolute;
	top: 10px;
	left: 10px;
	z-index: 10;
	display: flex;
	flex-direction: column;
	gap: 2px;
	background-color: rgba(40, 40, 50, 0.9);
	border-radius: 4px;
	padding: 4px;
`;

const MeasurementButton = styled.button`
	width: 36px;
	height: 36px;
	border: none;
	border-radius: 4px;
	background-color: ${props => (props.$active ? '#00ff00' : 'transparent')};
	color: white;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 1rem;
	transition: background-color 0.2s;

	&:hover {
		background-color: ${props => (props.$active ? '#00ff00' : 'rgba(60, 60, 70, 0.9)')};
	}
`;

// Base map layer configurations
const BASE_LAYERS = {
	osm: {
		name: 'OpenStreetMap',
		source: () => new OSM(),
	},
	satellite: {
		name: 'Satellite',
		maxZoom: 18,
		source: () =>
			new XYZ({
				url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
				maxZoom: 18,
			}),
	},
	terrain: {
		name: 'Terrain',
		maxZoom: 17,
		source: () =>
			new XYZ({
				// Using OpenTopoMap - free terrain/topographic tiles
				url: 'https://tile.opentopomap.org/{z}/{x}/{y}.png',
				maxZoom: 17,
				attributions: '© OpenTopoMap (CC-BY-SA)',
			}),
	},
};

// White color for polygon lines
const POLYGON_COLOR = '#ffffff';
const POLYGON_FILL = 'rgba(255, 255, 255, 0.15)';

// Polygon style with vertex points and midpoint markers
const createPolygonStyle = feature => {
	const styles = [
		// Fill and stroke for the polygon
		new Style({
			fill: new Fill({
				color: POLYGON_FILL,
			}),
			stroke: new Stroke({
				color: POLYGON_COLOR,
				width: 3,
			}),
		}),
	];

	// Add vertex points and midpoint markers
	const geometry = feature.getGeometry();
	if (geometry && geometry.getType() === 'Polygon') {
		const coordinates = geometry.getCoordinates()[0];
		const numPoints = coordinates.length - 1; // Last point is same as first

		for (let i = 0; i < numPoints; i++) {
			const coord = coordinates[i];
			const nextCoord = coordinates[(i + 1) % numPoints];

			// Vertex point (white circle)
			styles.push(
				new Style({
					geometry: new Point(coord),
					image: new CircleStyle({
						radius: 8,
						fill: new Fill({ color: '#ffffff' }),
						stroke: new Stroke({ color: POLYGON_COLOR, width: 2 }),
					}),
				})
			);

			// Midpoint marker (smaller circle with + icon for inserting points)
			const midX = (coord[0] + nextCoord[0]) / 2;
			const midY = (coord[1] + nextCoord[1]) / 2;
			styles.push(
				new Style({
					geometry: new Point([midX, midY]),
					image: new CircleStyle({
						radius: 6,
						fill: new Fill({ color: 'rgba(255, 255, 255, 0.6)' }),
						stroke: new Stroke({ color: '#ffffff', width: 1.5 }),
					}),
				})
			);
		}
	}

	return styles;
};

// Drawing style
const drawingStyle = new Style({
	fill: new Fill({
		color: 'rgba(0, 255, 255, 0.1)',
	}),
	stroke: new Stroke({
		color: POLYGON_COLOR,
		width: 2,
		lineDash: [10, 10],
	}),
	image: new CircleStyle({
		radius: 6,
		fill: new Fill({ color: POLYGON_COLOR }),
		stroke: new Stroke({ color: '#fff', width: 2 }),
	}),
});

// Waypoint style
const waypointStyle = new Style({
	image: new CircleStyle({
		radius: 10,
		fill: new Fill({ color: '#ff6b35' }),
		stroke: new Stroke({ color: '#fff', width: 2 }),
	}),
});

// Flight lines style - bright yellow
const flightLinesStyle = new Style({
	stroke: new Stroke({
		color: '#ffff00',
		width: 2,
		lineDash: [8, 4],
	}),
});

// DSP footprint style - bright yellow
const dspFootprintStyle = new Style({
	fill: new Fill({
		color: 'rgba(255, 255, 0, 0.15)',
	}),
	stroke: new Stroke({
		color: '#ffff00',
		width: 2,
	}),
});

// Starting point style - green indicator
const startingPointStyle = new Style({
	image: new CircleStyle({
		radius: 14,
		fill: new Fill({ color: '#00ff00' }),
		stroke: new Stroke({ color: '#ffffff', width: 3 }),
	}),
});

const MapInterface = ({
	polygonPoints,
	onPolygonChange,
	waypoints,
	onWaypointsChange,
	flightLines,
	dspFootprints,
	drawingEnabled,
	polygonEditEnabled,
	onDrawingComplete,
	waypointMode,
	manualDspMode,
	startingPointIndex = 0,
	onStartingPointChange,
	onMeasurementsChange,
	measurements: externalMeasurements = [],
	airspaceLayers = {
		airports: true,
		military: true,
		heliports: true,
		government: true,
		nature: false,
		property: false,
		customMap: false,
	},
	layerOpacity = { nature: 0.6, property: 0.8, customMap: 0.7 },
	propertyLineWidth = 2,
	customMapConfig = null, // { url, username, password, layerName }
	initialCenter = [10.75, 59.91], // Default to Norway (Oslo area)
	initialZoom = 6,
	fitTrigger = 0, // Increment to trigger fit-to-polygon zoom
	utmGridEnabled = false,
	utmGridSize = 1000,
	selectedGridSquares = [],
	onGridSquareToggle = null,
	gridSearchTarget = null,
}) => {
	const mapRef = useRef(null);
	const mapInstanceRef = useRef(null);
	const polygonLayerRef = useRef(null);
	const waypointLayerRef = useRef(null);
	const flightLinesLayerRef = useRef(null);
	const dspFootprintsLayerRef = useRef(null);
	const startingPointLayerRef = useRef(null);
	const measurementLayerRef = useRef(null);
	const drawInteractionRef = useRef(null);
	const modifyInteractionRef = useRef(null);
	const waypointModifyRef = useRef(null);
	const snapInteractionRef = useRef(null);
	const measureDrawRef = useRef(null);
	const measureTooltipRef = useRef(null);
	const utmGridLayerRef = useRef(null);

	// Airspace layer refs
	const airspaceLayersRef = useRef({});

	const [activeBaseLayer, setActiveBaseLayer] = useState('satellite');
	const activeBaseLayerRef = useLatestRef(activeBaseLayer);
	const [mouseCoords, setMouseCoords] = useState(null);
	const [isActivelyDrawing, setIsActivelyDrawing] = useState(false);
	const [gridNeedsZoom, setGridNeedsZoom] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const [activeMeasureTool, setActiveMeasureTool] = useState(null); // 'distance', 'area', 'diameter'
	const [measurements, setMeasurements] = useState([]);
	const [livePolygonArea, setLivePolygonArea] = useState(null); // Real-time polygon area while drawing
	const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 }); // Mouse position relative to map container

	// Store refs for click handler to access current values
	// useLatestRef keeps each ref in sync with the latest prop value,
	// so event listeners set up once can always read the current value.
	const manualDspModeRef = useLatestRef(manualDspMode);
	const waypointsRef = useLatestRef(waypoints);
	const onWaypointsChangeRef = useLatestRef(onWaypointsChange);
	const onStartingPointChangeRef = useLatestRef(onStartingPointChange);
	const onGridSquareToggleRef = useLatestRef(onGridSquareToggle);
	const utmGridEnabledRef = useLatestRef(utmGridEnabled);
	const measurementsRef = useLatestRef(measurements);
	const onMeasurementsChangeRef = useLatestRef(onMeasurementsChange);

	// Initialize map
	useEffect(() => {
		if (!mapRef.current || mapInstanceRef.current) return;

		// Create polygon layer
		const polygonSource = new VectorSource();
		const polygonLayer = new VectorLayer({
			source: polygonSource,
			style: createPolygonStyle,
			zIndex: 10,
		});
		polygonLayerRef.current = polygonLayer;

		// Create waypoint layer
		const waypointSource = new VectorSource();
		const waypointLayer = new VectorLayer({
			source: waypointSource,
			style: waypointStyle,
			zIndex: 20,
		});
		waypointLayerRef.current = waypointLayer;

		// Create flight lines layer
		const flightLinesSource = new VectorSource();
		const flightLinesLayer = new VectorLayer({
			source: flightLinesSource,
			style: flightLinesStyle,
			zIndex: 5,
		});
		flightLinesLayerRef.current = flightLinesLayer;

		// Create DSP footprints layer
		const dspFootprintsSource = new VectorSource();
		const dspFootprintsLayer = new VectorLayer({
			source: dspFootprintsSource,
			style: dspFootprintStyle,
			zIndex: 15,
		});
		dspFootprintsLayerRef.current = dspFootprintsLayer;

		// Create starting point layer (green indicator)
		const startingPointSource = new VectorSource();
		const startingPointLayer = new VectorLayer({
			source: startingPointSource,
			style: startingPointStyle,
			zIndex: 25,
		});
		startingPointLayerRef.current = startingPointLayer;

		// Create measurement layer
		const measurementSource = new VectorSource();
		const measurementLayer = new VectorLayer({
			source: measurementSource,
			style: new Style({
				fill: new Fill({ color: 'rgba(255, 165, 0, 0.2)' }),
				stroke: new Stroke({ color: '#ffa500', width: 3 }),
				image: new CircleStyle({
					radius: 6,
					fill: new Fill({ color: '#ffa500' }),
					stroke: new Stroke({ color: '#fff', width: 2 }),
				}),
			}),
			zIndex: 30,
		});
		measurementLayerRef.current = measurementLayer;

		// Create Norwegian airspace layers
		const createAirspaceStyle = zoneType => {
			const config = ZONE_TYPES[zoneType];
			if (!config) {
				// Fallback style
				return new Style({
					fill: new Fill({ color: 'rgba(255, 0, 0, 0.3)' }),
					stroke: new Stroke({ color: '#ff0000', width: 2 }),
				});
			}
			return new Style({
				fill: new Fill({ color: config.color + '40' }), // 40 = 25% opacity in hex
				stroke: new Stroke({ color: config.color, width: 2 }),
			});
		};

		// Helper to create circle polygon manually
		const createCirclePolygon = (centerLon, centerLat, radiusMeters, numPoints = 64) => {
			const coords = [];
			const earthRadius = 6371000; // meters

			for (let i = 0; i <= numPoints; i++) {
				const angle = (i / numPoints) * 2 * Math.PI;
				const dx = radiusMeters * Math.cos(angle);
				const dy = radiusMeters * Math.sin(angle);

				// Convert meters to degrees (approximate)
				const dLat = (dy / earthRadius) * (180 / Math.PI);
				const dLon =
					(dx / (earthRadius * Math.cos((centerLat * Math.PI) / 180))) * (180 / Math.PI);

				coords.push(fromLonLat([centerLon + dLon, centerLat + dLat]));
			}

			return new Polygon([coords]);
		};

		const createAirspaceLayer = (zones, groupName, visible) => {
			const source = new VectorSource();

			zones.forEach(zone => {
				try {
					const circlePolygon = createCirclePolygon(zone.lng, zone.lat, zone.radius);

					const feature = new Feature({
						geometry: circlePolygon,
						name: zone.name,
						type: zone.type,
						radius: zone.radius,
						maxAlt: zone.maxAlt,
						notes: zone.notes,
						zoneId: zone.id,
					});
					source.addFeature(feature);
				} catch (e) {
					console.error('Error creating zone:', zone.name, e);
				}
			});

			const layer = new VectorLayer({
				source: source,
				style: createAirspaceStyle,
				zIndex: 2,
				visible: visible,
			});
			return layer;
		};

		// Create layers for each airspace group
		const airportLayer = createAirspaceLayer(airports, 'airports', true);
		const militaryLayer = createAirspaceLayer(militaryZones, 'military', true);
		const heliportLayer = createAirspaceLayer(heliports, 'heliports', true);
		const governmentLayer = createAirspaceLayer(governmentZones, 'government', true);

		// Create WMS layer for Norwegian nature reserves from Miljødirektoratet
		const natureWmsLayer = new TileLayer({
			source: new TileWMS({
				url: 'https://kart.miljodirektoratet.no/arcgis/services/vern/mapserver/WMSServer',
				params: {
					LAYERS: 'naturvern_omrade',
					TILED: true,
					FORMAT: 'image/png',
					TRANSPARENT: true,
				},
				serverType: 'mapserver',
				crossOrigin: 'anonymous',
			}),
			opacity: 0.6,
			zIndex: 2,
			visible: false, // Off by default
		});

		// Create WMS layer for Norwegian property/cadastral boundaries from Kartverket
		// Using wms.geonorge.no (openwms.statkart.no is deprecated)
		// Available layers: eiendomsgrense, grensepunkt, teig, hjelpelinje
		const propertyWmsLayer = new TileLayer({
			source: new TileWMS({
				url: 'https://wms.geonorge.no/skwms1/wms.matrikkelkart',
				params: {
					LAYERS: 'eiendomsgrense,grensepunkt',
					TILED: true,
					FORMAT: 'image/png',
					TRANSPARENT: true,
					VERSION: '1.3.0',
				},
				serverType: 'mapserver',
				crossOrigin: 'anonymous',
			}),
			opacity: 0.8,
			zIndex: 3,
			visible: false, // Off by default
		});

		// Create custom ArcGIS map layer (configurable per customer)
		let customMapLayer = null;
		let customMapAbortController = null;
		if (customMapConfig?.url) {
			// Create layer without a source initially — tiles only load after token is acquired
			customMapLayer = new TileLayer({
				opacity: 0.7,
				zIndex: 4,
				visible: false,
			});

			// Fetch ArcGIS token if credentials provided, then attach the source
			if (customMapConfig.username && customMapConfig.password) {
				customMapAbortController = new AbortController();
				// Timeout after 30s — don't let a hanging server block indefinitely
				const tokenTimeout = setTimeout(() => customMapAbortController.abort(), 30000);
				const tokenUrl = customMapConfig.url.replace(
					/\/rest\/services\/.*/,
					'/tokens/generateToken'
				);
				fetch(tokenUrl, {
					method: 'POST',
					signal: customMapAbortController.signal,
					headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
					body: new URLSearchParams({
						username: customMapConfig.username,
						password: customMapConfig.password,
						client: 'referer',
						referer: window.location.origin,
						f: 'json',
					}),
				})
					.then(res => res.json())
					.then(data => {
						clearTimeout(tokenTimeout);
						if (data.token) {
							customMapLayer.setSource(
								new TileArcGISRest({
									url: customMapConfig.url + '/MapServer',
									params: { token: data.token },
									crossOrigin: 'anonymous',
								})
							);
						} else {
							console.warn('Custom map token fetch: no token in response');
						}
					})
					.catch(err => {
						clearTimeout(tokenTimeout);
						if (err.name !== 'AbortError') {
							console.warn('Custom map token fetch failed:', err);
						}
					});
			} else {
				// No credentials — attach source directly without token
				customMapLayer.setSource(
					new TileArcGISRest({
						url: customMapConfig.url + '/MapServer',
						params: {},
						crossOrigin: 'anonymous',
					})
				);
			}
		}

		airspaceLayersRef.current = {
			airports: airportLayer,
			military: militaryLayer,
			heliports: heliportLayer,
			government: governmentLayer,
			nature: natureWmsLayer,
			property: propertyWmsLayer,
			customMap: customMapLayer,
		};

		// Create base layer
		const baseLayer = new TileLayer({
			source: BASE_LAYERS[activeBaseLayer].source(),
			zIndex: 0,
		});

		// Create map
		const map = new Map({
			target: mapRef.current,
			layers: [
				baseLayer,
				propertyWmsLayer,
				...(customMapLayer ? [customMapLayer] : []),
				airportLayer,
				militaryLayer,
				heliportLayer,
				governmentLayer,
				natureWmsLayer,
				flightLinesLayer,
				polygonLayer,
				dspFootprintsLayer,
				waypointLayer,
				startingPointLayer,
				measurementLayer,
			],
			view: new View({
				center: fromLonLat(initialCenter),
				zoom: initialZoom,
				projection: getProjection('EPSG:3857'),
				maxZoom: 20,
				minZoom: 3,
			}),
			controls: defaultControls({ zoom: false, rotate: false }),
			interactions: defaultInteractions({
				doubleClickZoom: false,
			}),
		});

		// Track mouse position and change cursor on hover over features
		map.on('pointermove', evt => {
			const coords = toLonLat(evt.coordinate);
			setMouseCoords({
				lat: coords[1].toFixed(6),
				lng: coords[0].toFixed(6),
			});

			// Change cursor to pointer when hovering over clickable features
			const pixel = map.getEventPixel(evt.originalEvent);
			const hit = map.hasFeatureAtPixel(pixel, {
				layerFilter: layer =>
					layer === waypointLayerRef.current ||
					layer === polygonLayerRef.current ||
					layer === utmGridLayerRef.current,
			});
			map.getTargetElement().style.cursor = hit ? 'pointer' : '';
		});

		// Create popup overlay for airspace zones
		const popupElement = document.createElement('div');
		popupElement.className = 'airspace-popup';
		popupElement.style.cssText = `
			background: rgba(20, 20, 30, 0.95);
			border: 2px solid #4CAF50;
			border-radius: 8px;
			padding: 12px 16px;
			max-width: 280px;
			box-shadow: 0 4px 20px rgba(0,0,0,0.5);
			font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
			display: none;
		`;

		const popup = new Overlay({
			element: popupElement,
			positioning: 'bottom-center',
			offset: [0, -10],
			stopEvent: true,
		});
		map.addOverlay(popup);

		// Handle clicks - check for airspace zones first
		map.on('click', async evt => {
			// Check if clicked on an airspace zone (vector layers)
			const airspaceFeature = map.forEachFeatureAtPixel(evt.pixel, f => f, {
				layerFilter: layer => {
					// Check vector layers only (not the WMS nature layer)
					const vectorLayers = ['airports', 'military', 'heliports', 'government'];
					return vectorLayers.some(key => airspaceLayersRef.current[key] === layer);
				},
			});

			if (airspaceFeature && airspaceFeature.get('zoneId')) {
				const name = airspaceFeature.get('name');
				const type = airspaceFeature.get('type');
				const radius = airspaceFeature.get('radius');
				const maxAlt = airspaceFeature.get('maxAlt');
				const notes = airspaceFeature.get('notes');

				// Get human-readable restriction message
				const getRestrictionMessage = zoneType => {
					switch (zoneType) {
						case 'AIRPORT':
						case 'AIRPORT_CTR':
							return '⛔ Drone flight prohibited without ATC permission. Contact the airport tower before flying.';
						case 'MILITARY':
							return '⛔ Military restricted area. Drone flight strictly prohibited. Violations may result in legal action.';
						case 'HELIPORT':
							return '⚠ Heliport zone. Drone flight restricted due to helicopter traffic. Maintain safe distance.';
						case 'HOSPITAL':
							return '⚠ Hospital helipad zone. Keep clear for air ambulance operations. Emergency flights have priority.';
						case 'GOVERNMENT':
							return '⛔ Government restricted area. Drone flight prohibited for security reasons.';
						case 'PRISON':
							return '⛔ Prison no-fly zone. Drone flight strictly prohibited. Violations are criminal offenses.';
						case 'NATURE_RESERVE':
							return '⚠ Nature reserve. Drone flight may disturb wildlife. Check seasonal restrictions before flying.';
						default:
							return '⚠ Restricted airspace. Check local regulations before flying.';
					}
				};

				const typeColors = {
					AIRPORT: '#FF0000',
					AIRPORT_CTR: '#FF4444',
					MILITARY: '#8B0000',
					HELIPORT: '#FF6600',
					HOSPITAL: '#0066CC',
					GOVERNMENT: '#4B0082',
					PRISON: '#800080',
					NATURE_RESERVE: '#228B22',
				};

				const typeLabels = {
					AIRPORT: 'Airport',
					AIRPORT_CTR: 'Airport CTR',
					MILITARY: 'Military Zone',
					HELIPORT: 'Heliport',
					HOSPITAL: 'Hospital Helipad',
					GOVERNMENT: 'Government Area',
					PRISON: 'Prison',
					NATURE_RESERVE: 'Nature Reserve',
				};

				popupElement.innerHTML = `
					<div style="display: flex; justify-content: space-between; align-items: start; margin-bottom: 8px;">
						<h4 style="margin: 0; color: #fff; font-size: 14px; flex: 1;">${name}</h4>
						<button onclick="this.parentElement.parentElement.style.display='none'"
							style="background: none; border: none; color: #888; cursor: pointer; font-size: 16px; padding: 0; margin-left: 8px;">×</button>
					</div>
					<div style="display: inline-block; padding: 3px 8px; border-radius: 4px; font-size: 10px; font-weight: 600; margin-bottom: 10px; background: ${typeColors[type] || '#666'}; color: white;">
						${typeLabels[type] || type}
					</div>
					<div style="font-size: 12px; color: #ccc; margin-bottom: 8px;">
						<div style="margin-bottom: 4px;">◉ Radius: <strong>${(radius / 1000).toFixed(1)} km</strong></div>
						${maxAlt ? `<div style="margin-bottom: 4px;">↕ Max altitude: <strong>${maxAlt}m</strong></div>` : ''}
						${notes ? `<div style="margin-bottom: 4px; color: #aaa; font-style: italic;">${notes}</div>` : ''}
					</div>
					<div style="font-size: 12px; color: #ff9800; background: rgba(255, 152, 0, 0.1); padding: 8px; border-radius: 4px; border-left: 3px solid #ff9800;">
						${getRestrictionMessage(type)}
					</div>
				`;

				popupElement.style.display = 'block';
				popup.setPosition(evt.coordinate);
				return;
			}

			// Check if nature WMS layer is visible and query it
			const natureLayer = airspaceLayersRef.current.nature;
			if (natureLayer && natureLayer.getVisible()) {
				const viewResolution = map.getView().getResolution();
				const source = natureLayer.getSource();
				const url = source.getFeatureInfoUrl(evt.coordinate, viewResolution, 'EPSG:3857', {
					INFO_FORMAT: 'text/html',
					QUERY_LAYERS: 'naturvern_omrade',
				});

				if (url) {
					try {
						// Show loading state
						popupElement.innerHTML = `
							<div style="color: #888; font-size: 12px; padding: 8px;">
								Loading nature reserve info...
							</div>
						`;
						popupElement.style.display = 'block';
						popup.setPosition(evt.coordinate);

						const response = await fetch(url);
						const htmlText = await response.text();

						// Parse the HTML response
						const parser = new DOMParser();
						const doc = parser.parseFromString(htmlText, 'text/html');

						// Extract data from the response
						let name = '';
						let verneform = '';
						let vernedato = '';
						let forvaltningsmyndighet = '';
						let areal = '';
						let iucn = '';
						let foundData = false;

						// Method 1: Look for table rows (ArcGIS MapServer format)
						const rows = doc.querySelectorAll('tr');
						rows.forEach(row => {
							const cells = row.querySelectorAll('td, th');
							if (cells.length >= 2) {
								const label = (cells[0].textContent || '').toLowerCase().trim();
								const value = (cells[1].textContent || '').trim();

								if (value && value.length > 0) {
									foundData = true;
									if (
										label.includes('navn') ||
										label.includes('name') ||
										label === 'omradenavn'
									) {
										name = value;
									} else if (
										label.includes('verneform') ||
										label === 'verneform'
									) {
										verneform = value;
									} else if (
										label.includes('vernedato') ||
										label.includes('vernet_dato') ||
										label === 'vernet'
									) {
										vernedato = value;
									} else if (
										label.includes('forvaltningsmyndighet') ||
										label.includes('forvaltning') ||
										label === 'forvalter'
									) {
										forvaltningsmyndighet = value;
									} else if (
										label.includes('areal') ||
										label === 'landareal' ||
										label === 'totalareal'
									) {
										areal = value;
									} else if (label.includes('iucn')) {
										iucn = value;
									}
								}
							}
						});

						// Method 2: Look for definition lists (dl/dt/dd)
						const dts = doc.querySelectorAll('dt');
						dts.forEach(dt => {
							const dd = dt.nextElementSibling;
							if (dd && dd.tagName === 'DD') {
								const label = (dt.textContent || '').toLowerCase().trim();
								const value = (dd.textContent || '').trim();
								if (value) {
									foundData = true;
									if (label.includes('navn')) name = value;
									if (label.includes('verneform')) verneform = value;
									if (label.includes('vernedato')) vernedato = value;
									if (label.includes('forvaltning'))
										forvaltningsmyndighet = value;
									if (label.includes('areal')) areal = value;
								}
							}
						});

						// Method 3: Check body text for any nature reserve indicators
						const bodyText = doc.body?.textContent || '';
						if (
							!foundData &&
							(bodyText.includes('naturreservat') ||
								bodyText.includes('nasjonalpark') ||
								bodyText.includes('landskapsvernområde'))
						) {
							foundData = true;
							// Try to extract name from first meaningful text
							const textLines = bodyText
								.split('\n')
								.map(l => l.trim())
								.filter(l => l.length > 3);
							if (textLines.length > 0) {
								name = textLines[0].substring(0, 50);
							}
						}

						// Only show popup if we found actual data
						if (foundData) {
							popupElement.innerHTML = `
								<div style="display: flex; justify-content: space-between; align-items: start; margin-bottom: 8px;">
									<h4 style="margin: 0; color: #fff; font-size: 14px; flex: 1;">${name || 'Nature Reserve'}</h4>
									<button onclick="this.parentElement.parentElement.style.display='none'"
										style="background: none; border: none; color: #888; cursor: pointer; font-size: 16px; padding: 0; margin-left: 8px;">×</button>
								</div>
								<div style="display: inline-block; padding: 3px 8px; border-radius: 4px; font-size: 10px; font-weight: 600; margin-bottom: 10px; background: #228B22; color: white;">
									${verneform || 'Protected Area'}
								</div>
								<div style="font-size: 12px; color: #ccc; margin-bottom: 8px;">
									${vernedato ? `<div style="margin-bottom: 4px;">◷ Protected since: <strong>${vernedato}</strong></div>` : ''}
									${areal ? `<div style="margin-bottom: 4px;">▢ Area: <strong>${areal}</strong></div>` : ''}
									${forvaltningsmyndighet ? `<div style="margin-bottom: 4px;">⌂ Managed by: <strong>${forvaltningsmyndighet}</strong></div>` : ''}
									${iucn ? `<div style="margin-bottom: 4px;">◈ IUCN Category: <strong>${iucn}</strong></div>` : ''}
								</div>
								<div style="font-size: 12px; color: #ff9800; background: rgba(255, 152, 0, 0.1); padding: 8px; border-radius: 4px; border-left: 3px solid #ff9800;">
									⚠ Protected nature area. Drone flight may disturb wildlife and is often restricted. Check with the management authority before flying.
								</div>
							`;
							return;
						}

						// No feature found at this location
						popupElement.style.display = 'none';
					} catch (error) {
						console.error('Error fetching WMS feature info:', error);
						popupElement.style.display = 'none';
					}
				}
			}

			// Hide popup if clicking elsewhere
			popupElement.style.display = 'none';

			// Handle UTM grid square click
			if (
				utmGridEnabledRef.current &&
				onGridSquareToggleRef.current &&
				utmGridLayerRef.current
			) {
				const clickedFeature = map.forEachFeatureAtPixel(evt.pixel, f => f, {
					layerFilter: layer => layer === utmGridLayerRef.current,
				});
				if (clickedFeature) {
					const squareData = clickedFeature.get('gridSquare');
					if (squareData) {
						onGridSquareToggleRef.current(squareData);
						return; // Don't process other click handlers
					}
				}
			}

			// Only add waypoints in manual DSP mode (using ref to get current value)
			if (manualDspModeRef.current && onWaypointsChangeRef.current) {
				const coords = toLonLat(evt.coordinate);
				const newWaypoint = {
					latitude: coords[1],
					longitude: coords[0],
				};
				const currentWaypoints = waypointsRef.current || [];
				onWaypointsChangeRef.current([...currentWaypoints, newWaypoint]);
			}
		});

		// Handle double-click on waypoint to set as starting point
		map.on('dblclick', evt => {
			const pixel = map.getEventPixel(evt.originalEvent);
			const feature = map.forEachFeatureAtPixel(pixel, f => f, {
				layerFilter: layer => layer === waypointLayerRef.current,
			});

			if (feature && feature.get('sequence') !== undefined) {
				const waypointIndex = feature.get('sequence') - 1;
				if (onStartingPointChangeRef.current) {
					onStartingPointChangeRef.current(waypointIndex);
				}
				evt.preventDefault();
				evt.stopPropagation();
			}
		});

		mapInstanceRef.current = map;

		// Keyboard handler for Escape to cancel drawing
		const handleKeyDown = evt => {
			if (evt.key === 'Escape' && drawInteractionRef.current) {
				drawInteractionRef.current.abortDrawing();
				setIsActivelyDrawing(false);
			}
		};
		document.addEventListener('keydown', handleKeyDown);

		return () => {
			if (customMapAbortController) customMapAbortController.abort();
			document.removeEventListener('keydown', handleKeyDown);
			map.setTarget(null);
			mapInstanceRef.current = null;
		};
	}, []);

	// Update base layer when changed
	useEffect(() => {
		if (!mapInstanceRef.current) return;

		const map = mapInstanceRef.current;
		const layers = map.getLayers().getArray();
		const baseLayer = layers.find(l => l.getZIndex() === 0);

		if (baseLayer) {
			baseLayer.setSource(BASE_LAYERS[activeBaseLayer].source());
		}
	}, [activeBaseLayer]);

	// Toggle airspace layer visibility
	useEffect(() => {
		Object.entries(airspaceLayers).forEach(([group, visible]) => {
			const layer = airspaceLayersRef.current[group];
			if (layer) {
				layer.setVisible(visible);
			}
		});
	}, [airspaceLayers]);

	// Update WMS layer opacity
	useEffect(() => {
		Object.entries(layerOpacity).forEach(([layerKey, opacity]) => {
			const layer = airspaceLayersRef.current[layerKey];
			if (layer) {
				layer.setOpacity(opacity);
			}
		});
	}, [layerOpacity]);

	// Update property layer line width via SLD
	useEffect(() => {
		const propertyLayer = airspaceLayersRef.current.property;
		if (propertyLayer) {
			const source = propertyLayer.getSource();
			// Create SLD_BODY for custom line styling
			const sldBody = `<?xml version="1.0" encoding="UTF-8"?>
<StyledLayerDescriptor version="1.0.0" xmlns="http://www.opengis.net/sld">
  <NamedLayer>
    <Name>eiendomsgrense</Name>
    <UserStyle>
      <FeatureTypeStyle>
        <Rule>
          <LineSymbolizer>
            <Stroke>
              <CssParameter name="stroke">#FF6600</CssParameter>
              <CssParameter name="stroke-width">${propertyLineWidth}</CssParameter>
            </Stroke>
          </LineSymbolizer>
        </Rule>
      </FeatureTypeStyle>
    </UserStyle>
  </NamedLayer>
</StyledLayerDescriptor>`;
			source.updateParams({ SLD_BODY: sldBody });
		}
	}, [propertyLineWidth]);

	// Handle drawing interaction
	useEffect(() => {
		if (!mapInstanceRef.current || !polygonLayerRef.current) return;

		const map = mapInstanceRef.current;
		const source = polygonLayerRef.current.getSource();

		// Remove existing draw interaction
		if (drawInteractionRef.current) {
			map.removeInteraction(drawInteractionRef.current);
			drawInteractionRef.current = null;
		}

		// Only add draw interaction when drawingEnabled is true
		if (drawingEnabled && !waypointMode && !manualDspMode) {
			// Add draw interaction
			const draw = new Draw({
				source: source,
				type: 'Polygon',
				style: drawingStyle,
			});

			draw.on('drawstart', evt => {
				setIsActivelyDrawing(true);
				setLivePolygonArea(null);

				// Track geometry changes for live area display
				const sketch = evt.feature;
				sketch.getGeometry().on('change', e => {
					const geom = e.target;
					if (geom.getType() === 'Polygon') {
						const area = getGeodesicArea(geom, { projection: 'EPSG:3857' });
						if (area > 0) {
							setLivePolygonArea(area);
						}
					}
				});
			});

			draw.on('drawend', evt => {
				setIsActivelyDrawing(false);
				setLivePolygonArea(null);
				const feature = evt.feature;
				const geometry = feature.getGeometry();
				const coords = geometry.getCoordinates()[0];

				// Convert to lat/lng
				const points = coords.slice(0, -1).map(coord => {
					const lonLat = toLonLat(coord);
					return { lat: lonLat[1], lng: lonLat[0] };
				});

				// Clear previous polygon (but don't re-add the feature - Draw already adds it)
				const existingFeatures = source.getFeatures();
				existingFeatures.forEach(f => {
					if (f !== feature) {
						source.removeFeature(f);
					}
				});

				if (onPolygonChange) {
					onPolygonChange(points);
				}

				// Remove draw interaction immediately after completion
				map.removeInteraction(draw);
				drawInteractionRef.current = null;

				// Reset cursor to default
				map.getTargetElement().style.cursor = '';

				// Signal that drawing is complete (use setTimeout to ensure state updates properly)
				setTimeout(() => {
					if (onDrawingComplete) {
						onDrawingComplete();
					}
				}, 0);
			});

			draw.on('drawabort', () => {
				setIsActivelyDrawing(false);
			});

			map.addInteraction(draw);
			drawInteractionRef.current = draw;
		}

		return () => {
			if (drawInteractionRef.current) {
				map.removeInteraction(drawInteractionRef.current);
				drawInteractionRef.current = null;
			}
		};
	}, [drawingEnabled, waypointMode, manualDspMode, onPolygonChange, onDrawingComplete]);

	// Always allow polygon editing when polygonEditEnabled is true
	useEffect(() => {
		if (!mapInstanceRef.current || !polygonLayerRef.current) return;

		const map = mapInstanceRef.current;
		const source = polygonLayerRef.current.getSource();

		// Remove existing modify/snap interactions
		if (modifyInteractionRef.current) {
			map.removeInteraction(modifyInteractionRef.current);
			modifyInteractionRef.current = null;
		}
		if (snapInteractionRef.current) {
			map.removeInteraction(snapInteractionRef.current);
			snapInteractionRef.current = null;
		}

		// Add modify interaction when polygon editing is enabled and polygon exists
		if (polygonEditEnabled && polygonPoints && polygonPoints.length >= 3) {
			const modify = new Modify({
				source: source,
				insertVertexCondition: () => true, // Allow inserting vertices by clicking on edges
			});
			modify.on('modifyend', () => {
				const features = source.getFeatures();
				if (features.length > 0) {
					const geometry = features[0].getGeometry();
					const coords = geometry.getCoordinates()[0];
					const points = coords.slice(0, -1).map(coord => {
						const lonLat = toLonLat(coord);
						return { lat: lonLat[1], lng: lonLat[0] };
					});
					if (onPolygonChange) {
						onPolygonChange(points);
					}
				}
			});
			map.addInteraction(modify);
			modifyInteractionRef.current = modify;

			const snap = new Snap({ source: source });
			map.addInteraction(snap);
			snapInteractionRef.current = snap;
		}

		return () => {
			if (modifyInteractionRef.current) {
				map.removeInteraction(modifyInteractionRef.current);
				modifyInteractionRef.current = null;
			}
			if (snapInteractionRef.current) {
				map.removeInteraction(snapInteractionRef.current);
				snapInteractionRef.current = null;
			}
		};
	}, [polygonEditEnabled, polygonPoints, onPolygonChange]);

	// Sync polygon points to map
	useEffect(() => {
		if (!polygonLayerRef.current || !polygonPoints) return;

		const source = polygonLayerRef.current.getSource();

		if (polygonPoints.length >= 3) {
			// Convert points to map coordinates
			const coords = polygonPoints.map(p => fromLonLat([p.lng, p.lat]));
			// Close the polygon
			coords.push(coords[0]);

			const existingFeatures = source.getFeatures();
			if (existingFeatures.length === 0) {
				// No existing feature — add one
				const polygon = new Polygon([coords]);
				const feature = new Feature({ geometry: polygon });
				source.addFeature(feature);
			} else {
				// Update existing feature geometry (for grid/import changes)
				existingFeatures[0].getGeometry().setCoordinates([coords]);
			}
		} else {
			// Clear polygon if fewer than 3 points
			source.clear();
		}
	}, [polygonPoints]);

	// UTM Grid layer management
	useEffect(() => {
		const map = mapInstanceRef.current;
		if (!map) return;

		// Remove existing grid layers
		if (utmGridLayerRef.current) {
			map.removeLayer(utmGridLayerRef.current);
			utmGridLayerRef.current = null;
		}

		if (!utmGridEnabled) {
			setGridNeedsZoom(false);
			return;
		}

		// Create grid layer (unselected squares)
		const gridSource = new VectorSource();
		const gridLayer = new VectorLayer({
			source: gridSource,
			zIndex: 6,
			style: feature => {
				const isSelected = feature.get('selected');
				return new Style({
					stroke: new Stroke({
						color: isSelected ? 'rgba(52, 211, 153, 0.9)' : 'rgba(255, 255, 255, 0.25)',
						width: isSelected ? 2 : 0.75,
					}),
					fill: new Fill({
						color: 'transparent',
					}),
					text: isSelected
						? new Text({
								text: feature.get('label') || '',
								font: '10px sans-serif',
								fill: new Fill({ color: 'rgba(52, 211, 153, 0.7)' }),
								stroke: new Stroke({ color: 'rgba(0, 0, 0, 0.6)', width: 2 }),
								overflow: true,
							})
						: undefined,
				});
			},
		});
		utmGridLayerRef.current = gridLayer;
		map.addLayer(gridLayer);

		// Function to update grid squares based on current view
		const updateGrid = () => {
			const view = map.getView();
			const zoom = view.getZoom();

			// Only show grid at appropriate zoom levels
			const minZoom = getZoomForGridSize(utmGridSize, 14);
			if (zoom < minZoom) {
				gridSource.clear();
				setGridNeedsZoom(true);
				return;
			}

			setGridNeedsZoom(false);

			const extent = view.calculateExtent(map.getSize());
			const sw = toLonLat([extent[0], extent[1]]);
			const ne = toLonLat([extent[2], extent[3]]);

			const squares = generateGridSquares(
				{ minLat: sw[1], maxLat: ne[1], minLng: sw[0], maxLng: ne[0] },
				utmGridSize
			);

			// Build set of selected IDs for quick lookup
			const selectedIds = new Set(selectedGridSquares.map(s => s.id));

			gridSource.clear();
			squares.forEach(sq => {
				const coords = sq.corners.map(c => fromLonLat([c.lng, c.lat]));
				coords.push(coords[0]); // Close polygon
				const feature = new Feature({
					geometry: new Polygon([coords]),
					gridSquare: sq,
					label: sq.label,
					selected: selectedIds.has(sq.id),
				});
				gridSource.addFeature(feature);
			});
		};

		// Initial render
		updateGrid();

		// Update on map move
		const moveEndKey = map.on('moveend', updateGrid);

		return () => {
			if (utmGridLayerRef.current) {
				map.removeLayer(utmGridLayerRef.current);
				utmGridLayerRef.current = null;
			}
			if (moveEndKey) {
				map.un('moveend', updateGrid);
			}
		};
	}, [utmGridEnabled, utmGridSize, selectedGridSquares]);

	// Sync waypoints to map
	useEffect(() => {
		if (!waypointLayerRef.current || !waypoints) return;

		const source = waypointLayerRef.current.getSource();
		source.clear();

		if (waypoints.length > 0) {
			const features = waypoints.map((wp, index) => {
				const feature = new Feature({
					geometry: new Point(fromLonLat([wp.longitude, wp.latitude])),
					sequence: index + 1,
				});
				return feature;
			});
			source.addFeatures(features);
		}
	}, [waypoints]);

	// Sync starting point indicator to map
	// NOTE: We must clear the layer even when waypoints is empty, otherwise the
	// green starting-point marker persists visually after a mission reset.
	useEffect(() => {
		if (!startingPointLayerRef.current) return;

		const source = startingPointLayerRef.current.getSource();
		source.clear();

		if (!waypoints || waypoints.length === 0) return;

		// Show starting point indicator at the starting waypoint
		const startWp = waypoints[startingPointIndex] || waypoints[0];
		if (startWp) {
			const point = new Point(fromLonLat([startWp.longitude, startWp.latitude]));
			const feature = new Feature({
				geometry: point,
				isStartingPoint: true,
			});
			source.addFeature(feature);
		}
	}, [waypoints, startingPointIndex]);

	// Sync flight lines to map
	useEffect(() => {
		if (!flightLinesLayerRef.current) return;

		const source = flightLinesLayerRef.current.getSource();
		source.clear();

		if (flightLines && flightLines.length > 0) {
			const features = flightLines.map((line, index) => {
				const coords = line.map(p => fromLonLat([p.lng, p.lat]));
				const feature = new Feature({
					geometry: new LineString(coords),
					lineIndex: index,
				});
				return feature;
			});
			source.addFeatures(features);
		}
	}, [flightLines]);

	// Sync DSP footprints to map
	useEffect(() => {
		if (!dspFootprintsLayerRef.current) return;

		const source = dspFootprintsLayerRef.current.getSource();
		source.clear();

		if (dspFootprints && dspFootprints.length > 0) {
			const features = dspFootprints.map((footprint, index) => {
				const coords = footprint.corners.map(c => fromLonLat([c.lng, c.lat]));
				coords.push(coords[0]);
				const feature = new Feature({
					geometry: new Polygon([coords]),
					footprintIndex: index,
					altitude: footprint.altitude,
				});
				return feature;
			});
			source.addFeatures(features);
		}
	}, [dspFootprints]);

	// Handle waypoint dragging in waypoint mode
	useEffect(() => {
		if (!mapInstanceRef.current || !waypointLayerRef.current) return;

		const map = mapInstanceRef.current;
		const source = waypointLayerRef.current.getSource();

		// Remove existing waypoint modify interaction
		if (waypointModifyRef.current) {
			map.removeInteraction(waypointModifyRef.current);
			waypointModifyRef.current = null;
		}

		// Add modify interaction for waypoints when in waypoint mode
		if (waypointMode && waypoints && waypoints.length > 0) {
			const modify = new Modify({
				source: source,
				style: new Style({
					image: new CircleStyle({
						radius: 12,
						fill: new Fill({ color: 'rgba(255, 107, 53, 0.8)' }),
						stroke: new Stroke({ color: '#fff', width: 3 }),
					}),
				}),
			});

			modify.on('modifyend', () => {
				const features = source.getFeatures();
				// Sort by original sequence to preserve waypoint order
				const sorted = [...features].sort(
					(a, b) => (a.get('sequence') || 0) - (b.get('sequence') || 0)
				);
				const updatedWaypoints = sorted.map(feature => {
					const coords = toLonLat(feature.getGeometry().getCoordinates());
					return {
						latitude: coords[1],
						longitude: coords[0],
					};
				});
				if (onWaypointsChange) {
					onWaypointsChange(updatedWaypoints);
				}
			});

			map.addInteraction(modify);
			waypointModifyRef.current = modify;
		}

		return () => {
			if (waypointModifyRef.current) {
				map.removeInteraction(waypointModifyRef.current);
				waypointModifyRef.current = null;
			}
		};
	}, [waypointMode, waypoints, onWaypointsChange]);

	// Cancel current drawing
	const handleCancelDrawing = useCallback(() => {
		if (drawInteractionRef.current) {
			drawInteractionRef.current.abortDrawing();
			setIsActivelyDrawing(false);
		}
	}, []);

	// Clear polygon
	const handleClearPolygon = useCallback(() => {
		if (polygonLayerRef.current) {
			polygonLayerRef.current.getSource().clear();
		}
		if (onPolygonChange) {
			onPolygonChange([]);
		}
	}, [onPolygonChange]);

	// Clear waypoints
	const handleClearWaypoints = useCallback(() => {
		if (waypointLayerRef.current) {
			waypointLayerRef.current.getSource().clear();
		}
		if (onWaypointsChange) {
			onWaypointsChange([]);
		}
	}, [onWaypointsChange]);

	// Zoom controls
	const handleZoomIn = useCallback(() => {
		if (mapInstanceRef.current) {
			const view = mapInstanceRef.current.getView();
			view.animate({ zoom: view.getZoom() + 1, duration: 200 });
		}
	}, []);

	const handleZoomOut = useCallback(() => {
		if (mapInstanceRef.current) {
			const view = mapInstanceRef.current.getView();
			view.animate({ zoom: view.getZoom() - 1, duration: 200 });
		}
	}, []);

	// Fit to polygon
	const handleFitToPolygon = useCallback(() => {
		if (mapInstanceRef.current && polygonLayerRef.current) {
			const source = polygonLayerRef.current.getSource();
			const features = source.getFeatures();
			if (features.length > 0) {
				const extent = source.getExtent();
				// ArcGIS World Imagery returns placeholder tiles in rural areas
				// at high zoom levels. Cap auto-zoom for satellite to avoid this.
				const fitOptions = { padding: [50, 50, 50, 50], duration: 500 };
				const layerConfig = BASE_LAYERS[activeBaseLayerRef.current];
				if (layerConfig?.maxZoom) fitOptions.maxZoom = layerConfig.maxZoom;
				mapInstanceRef.current.getView().fit(extent, fitOptions);
			}
		}
	}, []);

	// Auto-fit when fitTrigger changes (e.g. loading a saved plan)
	useEffect(() => {
		if (fitTrigger > 0) {
			// Small delay to let polygon render first
			const timer = setTimeout(handleFitToPolygon, 200);
			return () => clearTimeout(timer);
		}
	}, [fitTrigger, handleFitToPolygon]);

	// Navigate to grid reference search target
	useEffect(() => {
		if (!gridSearchTarget || !mapInstanceRef.current) return;
		const view = mapInstanceRef.current.getView();
		view.animate({
			center: fromLonLat([gridSearchTarget.lng, gridSearchTarget.lat]),
			zoom: getZoomForGridSize(utmGridSize),
			duration: 500,
		});
	}, [gridSearchTarget]);

	// Search for address using Nominatim
	const handleSearch = useCallback(async () => {
		if (!searchQuery.trim() || !mapInstanceRef.current) return;

		try {
			const response = await fetch(
				`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`
			);
			const results = await response.json();

			if (results && results.length > 0) {
				const { lat, lon } = results[0];
				const view = mapInstanceRef.current.getView();
				view.animate({
					center: fromLonLat([parseFloat(lon), parseFloat(lat)]),
					zoom: 15,
					duration: 1000,
				});
			}
		} catch (error) {
			console.error('Search failed:', error);
		}
	}, [searchQuery]);

	// Handle search on Enter key
	const handleSearchKeyDown = useCallback(
		e => {
			if (e.key === 'Enter') {
				handleSearch();
			}
		},
		[handleSearch]
	);

	// Start measurement tool
	const handleMeasureTool = useCallback(
		tool => {
			if (!mapInstanceRef.current || !measurementLayerRef.current) return;

			const map = mapInstanceRef.current;
			const source = measurementLayerRef.current.getSource();

			// Remove existing measurement draw interaction
			if (measureDrawRef.current) {
				map.removeInteraction(measureDrawRef.current);
				measureDrawRef.current = null;
			}

			// Also cancel polygon drawing if active
			if (drawInteractionRef.current) {
				map.removeInteraction(drawInteractionRef.current);
				drawInteractionRef.current = null;
				setIsActivelyDrawing(false);
			}

			// Toggle off if same tool
			if (activeMeasureTool === tool) {
				setActiveMeasureTool(null);
				return;
			}

			setActiveMeasureTool(tool);

			let drawType = 'LineString';
			if (tool === 'area') drawType = 'Polygon';
			if (tool === 'diameter') drawType = 'Circle';

			// Style with endpoint markers
			const measureStyle = new Style({
				fill: new Fill({ color: 'rgba(255, 165, 0, 0.2)' }),
				stroke: new Stroke({ color: '#ffa500', width: 3 }),
				image: new CircleStyle({
					radius: 7,
					fill: new Fill({ color: '#ffa500' }),
					stroke: new Stroke({ color: '#fff', width: 2 }),
				}),
			});

			const draw = new Draw({
				source: source,
				type: drawType,
				style: measureStyle,
			});

			// Create tooltip overlay for real-time measurement
			const tooltipElement = document.createElement('div');
			tooltipElement.className = 'measure-tooltip';
			tooltipElement.style.cssText =
				'background-color: rgba(255, 165, 0, 0.95); color: #000; padding: 6px 10px; border-radius: 4px; font-size: 0.9rem; font-weight: 600; white-space: nowrap; box-shadow: 0 2px 8px rgba(0,0,0,0.3); pointer-events: none;';

			const tooltip = new Overlay({
				element: tooltipElement,
				offset: [15, 0],
				positioning: 'center-left',
			});
			map.addOverlay(tooltip);
			measureTooltipRef.current = tooltip;

			// Real-time measurement display
			draw.on('drawstart', evt => {
				const sketch = evt.feature;
				sketch.getGeometry().on('change', e => {
					const geom = e.target;
					let value = 0;
					let unit = 'm';
					let tooltipCoord;

					if (tool === 'distance' && geom.getType() === 'LineString') {
						value = geom.getLength();
						unit = value > 1000 ? 'km' : 'm';
						if (unit === 'km') value /= 1000;
						tooltipCoord = geom.getLastCoordinate();
					} else if (tool === 'area' && geom.getType() === 'Polygon') {
						value = getGeodesicArea(geom, { projection: 'EPSG:3857' });
						unit = value > 10000 ? 'ha' : 'm²';
						if (unit === 'ha') value /= 10000;
						tooltipCoord = geom.getInteriorPoint().getCoordinates();
					} else if (tool === 'diameter' && geom.getType() === 'Circle') {
						value = geom.getRadius() * 2;
						unit = value > 1000 ? 'km' : 'm';
						if (unit === 'km') value /= 1000;
						tooltipCoord = geom.getCenter();
					}

					// Update tooltip content and position
					const icon = tool === 'distance' ? '⎯' : tool === 'area' ? '▢' : '◯';
					tooltipElement.innerHTML = `${icon} ${value.toFixed(2)} ${unit}`;
					if (tooltipCoord) {
						tooltip.setPosition(tooltipCoord);
					}
				});
			});

			draw.on('drawend', evt => {
				const feature = evt.feature;
				const geometry = feature.getGeometry();
				let measurement = {
					type: tool,
					value: 0,
					unit: 'm',
					id: Date.now(),
					color: '#ffa500',
				};

				if (tool === 'distance') {
					measurement.value = geometry.getLength();
					measurement.unit = measurement.value > 1000 ? 'km' : 'm';
					if (measurement.unit === 'km') measurement.value /= 1000;

					// Add endpoint markers for distance lines
					const coords = geometry.getCoordinates();
					if (coords.length >= 2) {
						// Start point
						const startPoint = new Feature({ geometry: new Point(coords[0]) });
						startPoint.setStyle(
							new Style({
								image: new CircleStyle({
									radius: 8,
									fill: new Fill({ color: '#ffa500' }),
									stroke: new Stroke({ color: '#fff', width: 2 }),
								}),
							})
						);
						startPoint.set('measurementId', measurement.id);
						source.addFeature(startPoint);

						// End point
						const endPoint = new Feature({
							geometry: new Point(coords[coords.length - 1]),
						});
						endPoint.setStyle(
							new Style({
								image: new CircleStyle({
									radius: 8,
									fill: new Fill({ color: '#ffa500' }),
									stroke: new Stroke({ color: '#fff', width: 2 }),
								}),
							})
						);
						endPoint.set('measurementId', measurement.id);
						source.addFeature(endPoint);
					}
				} else if (tool === 'area') {
					measurement.value = getGeodesicArea(geometry, { projection: 'EPSG:3857' });
					measurement.unit = measurement.value > 10000 ? 'ha' : 'm²';
					if (measurement.unit === 'ha') measurement.value /= 10000;
				} else if (tool === 'diameter') {
					measurement.value = geometry.getRadius() * 2;
					measurement.unit = measurement.value > 1000 ? 'km' : 'm';
					if (measurement.unit === 'km') measurement.value /= 1000;
				}

				feature.set('measurement', measurement);
				feature.set('measurementId', measurement.id);
				const newMeasurements = [...measurementsRef.current, measurement];
				setMeasurements(newMeasurements);

				// Remove tooltip overlay
				if (measureTooltipRef.current) {
					map.removeOverlay(measureTooltipRef.current);
					measureTooltipRef.current = null;
				}

				if (onMeasurementsChangeRef.current)
					onMeasurementsChangeRef.current(newMeasurements);
			});

			draw.on('drawabort', () => {
				// Remove tooltip overlay
				if (measureTooltipRef.current) {
					map.removeOverlay(measureTooltipRef.current);
					measureTooltipRef.current = null;
				}
			});

			map.addInteraction(draw);
			measureDrawRef.current = draw;
		},
		[activeMeasureTool]
	);

	// Clear all measurements
	const handleClearMeasurements = useCallback(() => {
		if (measurementLayerRef.current) {
			measurementLayerRef.current.getSource().clear();
		}
		setMeasurements([]);
		if (onMeasurementsChange) {
			onMeasurementsChange([]);
		}
	}, [onMeasurementsChange]);

	// Delete a single measurement from map (called when parent deletes from list)
	const deleteMeasurementFromMap = useCallback(measurementId => {
		if (measurementLayerRef.current) {
			const source = measurementLayerRef.current.getSource();
			const features = source.getFeatures();
			const toRemove = [];
			features.forEach(f => {
				if (f.get('measurementId') === measurementId) {
					toRemove.push(f);
				}
			});
			toRemove.forEach(f => source.removeFeature(f));
		}
	}, []);

	// Watch for external measurement deletions (from parent component)
	const prevMeasurementsRef = useRef(externalMeasurements);
	useEffect(() => {
		const prevIds = new Set(prevMeasurementsRef.current.map(m => m.id));
		const currentIds = new Set(externalMeasurements.map(m => m.id));

		// If all measurements were cleared (e.g. "New Flight Plan" or "Clear all"), reset fully
		if (externalMeasurements.length === 0 && prevMeasurementsRef.current.length > 0) {
			if (measurementLayerRef.current) {
				measurementLayerRef.current.getSource().clear();
			}
			// Remove active measurement draw interaction
			if (measureDrawRef.current && mapInstanceRef.current) {
				mapInstanceRef.current.removeInteraction(measureDrawRef.current);
				measureDrawRef.current = null;
			}
			// Remove any lingering measurement tooltip overlay (won't fire drawabort when interaction is removed)
			if (measureTooltipRef.current && mapInstanceRef.current) {
				mapInstanceRef.current.removeOverlay(measureTooltipRef.current);
				measureTooltipRef.current = null;
			}
			setMeasurements([]);
			setActiveMeasureTool(null);
		} else {
			// Find individually deleted measurements
			prevIds.forEach(id => {
				if (!currentIds.has(id)) {
					deleteMeasurementFromMap(id);
				}
			});
			// Sync local state to match external (handles deletions from parent sidebar)
			if (currentIds.size < prevIds.size) {
				setMeasurements(externalMeasurements);
			}
		}

		prevMeasurementsRef.current = externalMeasurements;
	}, [externalMeasurements, deleteMeasurementFromMap]);

	return (
		<MapContainer
			ref={mapRef}
			onMouseMove={e => {
				const rect = e.currentTarget.getBoundingClientRect();
				setCursorPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
			}}>
			<MeasurementTools>
				<MeasurementButton
					$active={activeMeasureTool === 'distance'}
					onClick={() => handleMeasureTool('distance')}
					title="Measure distance">
					<LuRuler size={18} />
				</MeasurementButton>
				<MeasurementButton
					$active={activeMeasureTool === 'area'}
					onClick={() => handleMeasureTool('area')}
					title="Measure area">
					<LuSquareDashed size={18} />
				</MeasurementButton>
				<MeasurementButton
					$active={activeMeasureTool === 'diameter'}
					onClick={() => handleMeasureTool('diameter')}
					title="Measure diameter">
					<LuCircle size={18} />
				</MeasurementButton>
				{externalMeasurements.length > 0 && (
					<MeasurementButton
						onClick={handleClearMeasurements}
						title="Clear all measurements"
						style={{ marginTop: '4px' }}>
						<LuTrash size={16} />
					</MeasurementButton>
				)}
			</MeasurementTools>

			<MapControls>
				<ControlGroup>
					<ControlButton onClick={handleZoomIn} title="Zoom in">
						+
					</ControlButton>
					<ControlButton onClick={handleZoomOut} title="Zoom out">
						−
					</ControlButton>
				</ControlGroup>

				{isActivelyDrawing && (
					<ControlGroup>
						<ControlButton
							onClick={handleCancelDrawing}
							title="Cancel drawing (Esc)"
							style={{ backgroundColor: '#dc3545' }}>
							<LuX size={16} />
						</ControlButton>
					</ControlGroup>
				)}

				<ControlGroup>
					<ControlButton
						onClick={handleFitToPolygon}
						title="Fit to polygon"
						disabled={!polygonPoints || polygonPoints.length < 3}>
						<LuMaximize size={16} />
					</ControlButton>
					<ControlButton
						onClick={handleClearPolygon}
						title="Clear polygon"
						disabled={!polygonPoints || polygonPoints.length === 0}>
						<LuTrash size={16} />
					</ControlButton>
				</ControlGroup>

				{waypointMode && (
					<ControlGroup>
						<ControlButton
							onClick={handleClearWaypoints}
							title="Clear waypoints"
							disabled={!waypoints || waypoints.length === 0}>
							<LuMapPin size={16} />
						</ControlButton>
					</ControlGroup>
				)}
			</MapControls>

			<BottomBar>
				<LayerSelector>
					{Object.entries(BASE_LAYERS).map(([key, layer]) => (
						<LayerButton
							key={key}
							$active={activeBaseLayer === key}
							onClick={() => setActiveBaseLayer(key)}>
							{layer.name}
						</LayerButton>
					))}
				</LayerSelector>

				<div style={{ flex: 1 }} />

				<SearchContainer>
					<SearchInput
						type="text"
						placeholder="Search address..."
						value={searchQuery}
						onChange={e => setSearchQuery(e.target.value)}
						onKeyDown={handleSearchKeyDown}
					/>
					<SearchButton onClick={handleSearch} title="Search">
						<LuSearch size={16} />
					</SearchButton>
				</SearchContainer>

				{mouseCoords && (
					<CoordinateDisplay>
						{mouseCoords.lat}, {mouseCoords.lng}
					</CoordinateDisplay>
				)}
			</BottomBar>

			{utmGridEnabled && gridNeedsZoom && (
				<div
					style={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						background: 'rgba(0, 0, 0, 0.75)',
						color: '#94a3b8',
						padding: '12px 20px',
						borderRadius: '8px',
						fontSize: '0.85rem',
						textAlign: 'center',
						zIndex: 10,
						pointerEvents: 'none',
						backdropFilter: 'blur(4px)',
						border: '1px solid rgba(52, 211, 153, 0.2)',
					}}>
					<span style={{ color: '#34d399' }}>Zoom in</span> to see the UTM grid
				</div>
			)}

			{isActivelyDrawing && livePolygonArea !== null && (
				<div
					style={{
						position: 'absolute',
						left: cursorPos.x + 16,
						top: cursorPos.y - 40,
						background: 'rgba(0, 0, 0, 0.85)',
						color: '#00ff00',
						padding: '8px 14px',
						borderRadius: '6px',
						fontSize: '0.95rem',
						fontWeight: 'bold',
						zIndex: 1000,
						border: '1px solid #00ff00',
						pointerEvents: 'none',
						whiteSpace: 'nowrap',
					}}>
					{livePolygonArea > 10000
						? `${(livePolygonArea / 10000).toFixed(2)} ha`
						: `${livePolygonArea.toFixed(0)} m²`}
				</div>
			)}
		</MapContainer>
	);
};

export default MapInterface;
